import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEqual } from 'lodash-es';
import { IOrganizationUnitSelectListItemViewModel } from '@nimbus-global/features/shared/services/api/organization-unit/domain/i-organization-unit-select-list-item.view-model';
import { BaseHttpService } from '@nimbus-library/features/shared/services/base-http/base-http.service';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { PermissionsModel } from '@nimbus-global/features/permissions/models/permissions.model';

@UntilDestroy()
@Injectable()
export abstract class IOrganizationUnitDataService {
  protected organizationUnits$: BehaviorSubject<{ tenantId: string; organizationUnits: IOrganizationUnitSelectListItemViewModel[] }> =
    new BehaviorSubject(null);

  protected abstract tenantId$: Observable<string>;

  protected constructor(
    protected readonly baseHttpService: BaseHttpService,
    protected readonly authService: IAuthenticationService,
    protected readonly permissionManagerService: IPermissionManagerService,
  ) {}

  /**
   * Portal/Admin differs how they handle the tenantId therefore we have an abstraction here
   */
  public abstract getTenantId(): string;

  /**
   * Snapshot from the current state
   */
  public getOrganizationUnits(): Observable<IOrganizationUnitSelectListItemViewModel[]> {
    return this.organizationUnits$.pipe(
      filter((result) => !!result),
      take(1),
      map((result) => result.organizationUnits),
    );
  }

  /**
   * Organization unit stream
   */
  public getOrganizationUnits$(): Observable<IOrganizationUnitSelectListItemViewModel[]> {
    return this.organizationUnits$.asObservable().pipe(
      filter((result) => !!result),
      map((result) => result.organizationUnits),
    );
  }

  /**
   * Reload OUs whenever:
   * - the permission model changes: initial authentication + `permissionsChanged` SignalR event
   * - the active tenant changes: admin tenant switch
   */
  protected initialize(): void {
    combineLatest([
      this.permissionManagerService.getPermissionsModel$().pipe(
        filter((permissionsModel) => !!permissionsModel && permissionsModel !== PermissionsModel.Empty),
        distinctUntilChanged(isEqual),
      ),
      this.tenantId$.pipe(
        filter((tenantId) => !!tenantId && tenantId !== '0'),
        distinctUntilChanged(),
      ),
    ])
      .pipe(
        switchMap(([, tenantId]) => {
          if (this.organizationUnits$.value?.tenantId !== tenantId) {
            this.organizationUnits$.next(null);
          }
          return this.query(tenantId).pipe(map((organizationUnits) => ({ tenantId, organizationUnits })));
        }),
        untilDestroyed(this),
      )
      .subscribe((res) => {
        this.organizationUnits$.next(res);
      });
  }

  /**
   * Request to the BE
   * @param tenantId
   * @private
   */
  protected query(tenantId: string): Observable<IOrganizationUnitSelectListItemViewModel[]> {
    return this.baseHttpService.get(`facade/v1/common-portal/authorized-organization-units?tenantId=${tenantId}`);
  }
}
